// ═══════════════════════════════════════════════════════════════════════════════
// Usage summary page — daily quotas & total task slots (§22)
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { USER_LEVEL, USER_ROLE, type HelperUsageSummary, type UserUsageSummary } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { settingsApi } from '../../lib/api';
import { navigateBack } from '../../lib/navigateBack';
import { SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW } from '../../lib/layoutChrome';
import { EmployerTaskSubPageHeader } from '../../components/task/employer/EmployerTaskSubPageHeader';
import { UsageMetricCard } from '../../components/settings/UsageMetricCard';
import { MembershipPricingCompareModal } from '../../components/membership/MembershipPricingCompareModal';
import { HelpPopover } from '../../components/ui/HelpPopover';
import { canUseStripeBillingUi, isPlayStoreClient } from '../../lib/clientChannel';
import { usePublicConfig } from '../../hooks/usePublicConfig';

const levelLabelKey = (level: number): string => {
  if (level === USER_LEVEL.PAID) {
    return 'usage.levelPaid';
  }
  if (level === USER_LEVEL.FREE) {
    return 'usage.levelFree';
  }
  return 'usage.levelTrial';
};

type UsageTranslate = (key: string) => string;

type HelperMembershipHelpPopoverProps = {
  level: number;
  t: UsageTranslate;
};

const HelperMembershipHelpPopover = ({ level, t }: HelperMembershipHelpPopoverProps) => {
  const showContactEmployerUpgradeHint =
    level === USER_LEVEL.TRIAL || level === USER_LEVEL.FREE;

  return (
    <HelpPopover
      triggerAriaLabel={t('usage.helperMembershipHelpTriggerAria')}
      panelAriaLabel={t('usage.helperMembershipHelpAria')}
      estimatedPanelHeightPx={showContactEmployerUpgradeHint ? 160 : 120}
    >
      <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
        {t('usage.helperMembershipHelpTitle')}
      </p>
      <p className="mt-2 text-xs leading-relaxed text-gray-700 dark:text-gray-200">
        {t('usage.helperMembershipHelpFollowsEmployer')}
      </p>
      {showContactEmployerUpgradeHint ? (
        <p className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
          {t('usage.helperMembershipHelpContactEmployer')}
        </p>
      ) : null}
    </HelpPopover>
  );
};

type UsageLevelBadgeProps = {
  level: number;
  t: UsageTranslate;
  onCompareLevels?: () => void;
  showHelperMembershipHelp?: boolean;
};

const UsageLevelBadge = ({
  level,
  t,
  onCompareLevels,
  showHelperMembershipHelp = false,
}: UsageLevelBadgeProps) => (
  <div
    className="flex items-center justify-between gap-3 rounded-lg border border-primary-200 bg-primary-50 px-3 py-3 dark:border-primary-900 dark:bg-primary-950/30"
    role="status"
  >
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <p className="text-xs text-gray-500 dark:text-gray-400">{t('usage.membershipLevel')}</p>
        {showHelperMembershipHelp ? <HelperMembershipHelpPopover level={level} t={t} /> : null}
      </div>
      <p className="mt-0.5 text-sm font-semibold text-primary-800 dark:text-primary-200">
        {t(levelLabelKey(level))}
      </p>
    </div>
    {onCompareLevels ? (
      <button
        type="button"
        onClick={onCompareLevels}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onCompareLevels();
          }
        }}
        tabIndex={0}
        aria-label={t('usage.compareLevels')}
        className="shrink-0 rounded-lg border border-primary-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary-700 shadow-sm transition hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-primary-800 dark:bg-gray-900 dark:text-primary-300 dark:hover:bg-primary-950/40"
      >
        {t('usage.compareLevels')}
      </button>
    ) : null}
  </div>
);

type HelperDailyUsageCardsProps = {
  usage: HelperUsageSummary;
  t: UsageTranslate;
  showLevelBadge?: boolean;
};

const HelperDailyUsageCards = ({
  usage,
  t,
  showLevelBadge = true,
}: HelperDailyUsageCardsProps) => (
  <div className="space-y-3">
    {showLevelBadge ? (
      <UsageLevelBadge level={usage.level} t={t} showHelperMembershipHelp />
    ) : null}
    {usage.taskSubmit ? (
      <UsageMetricCard
        title={t('usage.taskSubmitTitle')}
        hint={t('usage.taskSubmitHint')}
        used={usage.taskSubmit.usedToday}
        limit={usage.taskSubmit.dailyLimit}
        resetAt={usage.taskSubmit.resetAt}
        resetAtLabel={t('usage.resetAtLabel')}
        unlimitedLabel={t('usage.unlimited')}
        loadingLabel={t('settings.aiUsageLoading')}
      />
    ) : null}
    {usage.ai ? (
      <UsageMetricCard
        title={t('settings.aiUsageTitle')}
        hint={t('settings.aiUsageHint')}
        used={usage.ai.usedToday}
        limit={usage.ai.dailyLimit}
        resetAt={usage.ai.resetAt}
        resetAtLabel={t('usage.resetAtLabel')}
        unlimitedLabel={t('usage.unlimited')}
        loadingLabel={t('settings.aiUsageLoading')}
      />
    ) : null}
    {usage.receipt ? (
      <UsageMetricCard
        title={t('settings.receiptUsageTitle')}
        hint={t('settings.receiptUsageHint')}
        used={usage.receipt.usedToday}
        limit={usage.receipt.dailyLimit}
        resetAt={usage.receipt.resetAt}
        resetAtLabel={t('usage.resetAtLabel')}
        unlimitedLabel={t('usage.unlimited')}
        loadingLabel={t('settings.aiUsageLoading')}
      />
    ) : null}
  </div>
);

const HelperUsageSectionDivider = ({ t }: { t: UsageTranslate }) => (
  <div className="relative py-2" role="separator" aria-label={t('usage.helperSectionTitle')}>
    <div className="absolute inset-0 flex items-center" aria-hidden="true">
      <div className="w-full border-t border-gray-200 dark:border-gray-700" />
    </div>
    <div className="relative flex justify-center">
      <span className="bg-[#f7f8fb] px-3 text-sm font-medium text-gray-600 dark:bg-gray-950 dark:text-gray-300">
        {t('usage.helperSectionTitle')}
      </span>
    </div>
  </div>
);

type UpgradeBannerProps = {
  t: UsageTranslate;
  onNavigateSubscription: () => void;
};

const EmployerPlayStoreUpgradeNotice = ({ t }: { t: UsageTranslate }) => (
  <div
    className="flex items-center gap-2 rounded-xl border border-yellow-300 bg-yellow-50 px-4 py-3 text-yellow-700 dark:border-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-200"
    role="status"
    aria-live="polite"
    aria-label={t('usage.playStoreUpgradeNotice')}
  >
    <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
    <span className="text-sm font-medium">{t('usage.playStoreUpgradeNotice')}</span>
  </div>
);

const EmployerUpgradeBanner = ({ t, onNavigateSubscription }: UpgradeBannerProps) => (
  <div
    className="flex items-center gap-2 rounded-xl border border-yellow-300 bg-yellow-50 px-4 py-3 text-yellow-700 dark:border-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-200"
    role="status"
    aria-live="polite"
    aria-label={`${t('usage.upgradeBannerBefore')}${t('usage.upgradeBannerClickHere')}${t('usage.upgradeBannerAfter')}`}
  >
    <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
    <span className="text-sm font-medium">
      {t('usage.upgradeBannerBefore')}
      <button
        type="button"
        onClick={onNavigateSubscription}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onNavigateSubscription();
          }
        }}
        tabIndex={0}
        aria-label={t('usage.upgradeBannerClickAria')}
        className="mx-0.5 font-bold text-primary-700 underline underline-offset-2 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-300 dark:hover:text-primary-200"
      >
        {t('usage.upgradeBannerClickHere')}
      </button>
      {t('usage.upgradeBannerAfter')}
    </span>
  </div>
);

export const UsagePage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isHelper = user?.role === USER_ROLE.HELPER;
  const isPlayClient = isPlayStoreClient();
  const { playStoreShowOfficialWebsiteUrl } = usePublicConfig(isPlayClient);

  const [usage, setUsage] = useState<UserUsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await settingsApi.getUsage();
        if (res.success && res.data) {
          setUsage(res.data);
        } else {
          setError(res.error ?? t('settings.dailyUsageError'));
        }
      } catch {
        setError(t('settings.dailyUsageError'));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [t]);

  const handleBack = () => {
    navigateBack(navigate, '/settings');
  };

  const handleOpenCompare = (): void => {
    setCompareOpen(true);
  };

  const handleCloseCompare = (): void => {
    setCompareOpen(false);
  };

  const handleNavigateSubscription = (): void => {
    navigate('/settings/subscription');
  };

  const showEmployerUpgradeBanner =
    !isHelper &&
    usage != null &&
    (usage.level === USER_LEVEL.TRIAL || usage.level === USER_LEVEL.FREE);
  const showStripeBillingUi = canUseStripeBillingUi();
  const showPlayStoreUpgradeNotice =
    isPlayClient && showEmployerUpgradeBanner && playStoreShowOfficialWebsiteUrl;

  return (
    <div className="min-h-full bg-[#f7f8fb] dark:bg-gray-950">
      <EmployerTaskSubPageHeader
        title={t('usage.pageTitle')}
        subtitle={t('usage.pageSubtitle')}
        onBack={handleBack}
      />

      <main className={`mx-auto max-w-lg space-y-4 px-4 py-6 ${SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW}`}>
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
        ) : null}

        {error ? (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300" role="alert">
            {error}
          </p>
        ) : null}

        {usage ? (
          <>
            {isHelper ? (
              <HelperDailyUsageCards usage={usage} t={t} />
            ) : (
              <>
                {showPlayStoreUpgradeNotice ? (
                  <EmployerPlayStoreUpgradeNotice t={t} />
                ) : null}
                {showEmployerUpgradeBanner && showStripeBillingUi ? (
                  <EmployerUpgradeBanner t={t} onNavigateSubscription={handleNavigateSubscription} />
                ) : null}
                <UsageLevelBadge level={usage.level} t={t} onCompareLevels={handleOpenCompare} />
                <div className="space-y-3">
                  {usage.taskReview ? (
                    <UsageMetricCard
                      title={t('usage.taskReviewTitle')}
                      hint={t('usage.taskReviewHint')}
                      used={usage.taskReview.usedToday}
                      limit={usage.taskReview.dailyLimit}
                      resetAt={usage.taskReview.resetAt}
                      resetAtLabel={t('usage.resetAtLabel')}
                      unlimitedLabel={t('usage.unlimited')}
                      loadingLabel={t('settings.aiUsageLoading')}
                    />
                  ) : null}
                  {usage.totalTasks ? (
                    <UsageMetricCard
                      title={t('usage.totalTasksTitle')}
                      hint={t('usage.totalTasksHint')}
                      used={usage.totalTasks.usedTotal}
                      limit={usage.totalTasks.maxTotalTasks}
                      unlimitedLabel={t('usage.unlimited')}
                      loadingLabel={t('settings.aiUsageLoading')}
                    />
                  ) : null}
                  {usage.pinMessages ? (
                    <UsageMetricCard
                      title={t('usage.pinMessagesTitle')}
                      hint={t('usage.pinMessagesHint')}
                      used={usage.pinMessages.usedTotal}
                      limit={usage.pinMessages.maxPinMessages}
                      unlimitedLabel={t('usage.unlimited')}
                      loadingLabel={t('settings.aiUsageLoading')}
                    />
                  ) : null}
                </div>
                {usage.helperUsage ? (
                  <>
                    <HelperUsageSectionDivider t={t} />
                    <HelperDailyUsageCards usage={usage.helperUsage} t={t} showLevelBadge={false} />
                  </>
                ) : null}
              </>
            )}
          </>
        ) : null}
      </main>

      {!isHelper ? (
        <MembershipPricingCompareModal open={compareOpen} onClose={handleCloseCompare} />
      ) : null}
    </div>
  );
};
